/*
 * @providesModule RewardSystemsTypes
 *
 */

import {ERewardSystemsVisualTreatment} from '@skillz/ui';
import {PrizeBoxRecordType} from 'SKZPrizeBox';

export const QUERY_KEYS = {
  rewardSystems: ['rewardSystems'],
} as const;

export const CONDITIONS_WITH_CASH_AMOUNT: TriggerConditionType[] = ['SINGLE_DEPOSIT_X_DOLLARS'];
export const DEFAULT_STEPS_FOR_CASH_CONDITIONS = 1;

type RewardSystemsType = 'CALENDAR' | 'SEQUENTIAL_CALENDAR' | 'NON_SKIP_CALENDAR';
type TriggerConditionType =
  | 'CONSECUTIVE_LOGIN_DAYS_GREATER_THAN'
  | 'DEPOSIT_X_TIMES'
  | 'PLAY_X_PRACTICE_TOURNAMENTS'
  | 'WIN_X_PRO_TOURNAMENTS'
  | 'DEPOSIT_CASH_BETWEEN'
  | 'SINGLE_DEPOSIT_X_DOLLARS'
  | 'PLAY_X_GAMES_WITH_ENTRY_ATLEAST_Y';

// eslint-disable-next-line no-shadow
export enum ECurrencyCode {
  VIRTUAL = 'VIRTUAL',
  TICKETZ = 'TICKETZ',
  USD = 'USD',
  INR = 'INR',
}

export type PrizeBoxRewardType = PrizeBoxRecordType | 'BONUS_CASH';

export type RewardSystemTriggerCondition = {
  type: TriggerConditionType;
  condition_id: string;
  order: number;
  max_amount: number;
  min_amount?: number;
  current_amount: number;
  frequency: number;
  current_frequency: number;
  visual_treatment: ERewardSystemsVisualTreatment;
  can_claim: boolean;
  claimed_at: string | null;
  description: string;
  reward: RewardItem | null;
};

export type RewardSystemItem = {
  name: string;
  description: string;
  type: RewardSystemsType;
  expiration: string;
  streak_subtitle: string;
  streak_end_subtitle: string;
  next_claim_time: string;
  duration?: number;
  conditions: RewardSystemTriggerCondition[];
};

export type RewardSystems = {
  reward_systems: RewardSystemItem[];
};

export type RewardItem = {
  type: PrizeBoxRewardType;
  amount: number | null;
};

export const PrizeToCurrencyMap = new Map<PrizeBoxRewardType, ECurrencyCode>([
  ['TICKETZ', ECurrencyCode.TICKETZ],
  ['CASH', ECurrencyCode.USD],
  ['BONUS_CASH', ECurrencyCode.USD],
  ['Z', ECurrencyCode.VIRTUAL],
]);

export const PrizeToCurrencyLabelMap = new Map<PrizeBoxRewardType, string>([
  ['TICKETZ', 'Ticketz'],
  ['CASH', 'Cash'],
  ['BONUS_CASH', 'Cash'],
  ['Z', 'Z'],
]);

export type RewardSystemTotalsType = {
  totalMissed: Map<ECurrencyCode, number>;
  totalEarned: Map<ECurrencyCode, number>;
};

export const parsePrizeBoxType = (type: PrizeBoxRewardType | undefined) => {
  if (type === 'BONUS_CASH') {
    return 'CASH';
  }

  return type ? type : 'NONE';
};

export const DEPOSIT_CONDITIONS = [
  'CUMULATIVE_DEPOSIT_X_DOLLARS',
  'DEPOSIT_X_TIMES',
  'SINGLE_DEPOSIT_X_DOLLARS',
];

export const PRO_GAME_CONDITIONS = [
  'PLAY_X_GAMES_WITH_PRIZE_ATLEAST_Y',
  'PLAY_X_PRO_TOURNAMENTS',
  'WIN_X_GAMES_WITH_PRIZE_ATLEAST_Y',
  'WIN_X_PRO_TOURNAMENTS',
  'PLAY_X_GAMES_WITH_ENTRY_ATLEAST_Y',
];

export const PRACTICE_GAME_CONDITIONS = [
  'PLAY_X_PRACTICE_TOURNAMENTS',
  'WIN_X_PRACTICE_TOURNAMENTS',
];

// eslint-disable-next-line no-shadow
export enum CountdownState {
  upcoming,
  live,
  ending,
  none,
}

type CountdownColorPercentage = {
  percentage: number;
  color: string;
};

export const DEFAULT_COUNTDOWN_COLOR = '#4201CE';

/**
 * For now we're keeping it hardcoded, but we can make it dynamic through AP in the future
 */
export const COUNTDOWN_COLOR_PER_REMAINING_TIME_PERCENTAGE: CountdownColorPercentage[] = [
  // when 10% remaining
  {
    percentage: 10,
    color: '#EA0449',
  },
  // when 50% remaining
  {
    percentage: 50,
    color: '#DD6952',
  },
  // when 100% remaining (default)
  {
    percentage: 100,
    color: DEFAULT_COUNTDOWN_COLOR,
  },
];
